import type { NotFoundAction } from 'src/components/not-found/NotFoundState';

import { $resetChatThreadsState } from '../chat-state';

/**
 * Exits for a missing thread. A host that can close itself (the docked drawer,
 * the workflow side panel) sits on a document or workflow, so its exits reset
 * or close the panel and leave the page underneath alone. Without `onClose`
 * this returns undefined and `NotFoundState` keeps its router exits.
 *
 * Close also starts a new chat where the host can, so reopening the panel does
 * not land on the same missing thread.
 */
export function getChatNotFoundActions({
  onClose,
  onCreateNewChat,
  onGoBack,
}: {
  onClose: (() => void) | undefined;
  onCreateNewChat: (() => void) | undefined;
  onGoBack: (() => void) | undefined;
}): readonly NotFoundAction[] | undefined {
  if (!onClose) return undefined;

  const closeAction: NotFoundAction = {
    label: 'Close Chat',
    onClick: withReset(() => {
      onCreateNewChat?.();
      onClose();
    }),
  };

  if (onCreateNewChat)
    return [{ label: 'New Chat', onClick: withReset(onCreateNewChat) }, closeAction];
  if (onGoBack) return [{ label: 'Go Back', onClick: withReset(onGoBack) }, closeAction];
  return [closeAction];
}

function withReset(action: () => void): () => void {
  return () => {
    $resetChatThreadsState();
    action();
  };
}
